import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import UploadArea from './UploadArea';
import ColorSwatches from './ColorSwatches';
import BuyMeACoffeeButton from './BuyMeACoffeeButton';
import extractMainColors from '../utils/extractMainColors';
import { extractEmptyStateColors } from '../utils/colorExtraction';
import { formatColor } from '../utils/colorFormats';
import { useTheme } from '../hooks/useTheme';
import { useToast } from '../hooks/useToast';
import { FORMAT_OPTIONS, ANIMATION_TIMINGS } from '../constants';
import '../App.css';

function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="copy-icon">
      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
    </svg>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export default function Home() {
  const [colors, setColors] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [logoSrc, setLogoSrc] = useState('/logo.png');
  const [isLoading, setIsLoading] = useState(false);
  const [colorFormat, setColorFormat] = useState('hex');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showBMCTooltip, setShowBMCTooltip] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  // Custom hooks
  const [theme, toggleTheme] = useTheme();
  const { toast, toastHex, toastColors, toastExiting, show: showToast } = useToast();

  useEffect(() => {
    // Trigger page load animation
    setIsPageLoaded(true);
  }, []);

  useEffect(() => {
    const loadEmptyStateColors = async () => {
      try {
        const extracted = await extractEmptyStateColors();
        setColors(extracted);
      } catch (err) {
        // Error already logged in utility function
      }
    };
    loadEmptyStateColors();
  }, []);

  const handleImage = async (file) => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setIsLoading(true);
    try {
      const extracted = await extractMainColors(file);
      setColors(extracted);
      
    } catch (err) {
      console.error('Failed to extract colors:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async () => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
    }
    setIsLoading(false);
    // Re-extract colors from emptyState.png
    try {
      const extracted = await extractEmptyStateColors();
      setColors(extracted);
    } catch (err) {
      // Error already logged in utility function
    }
  };

  const handleLogoError = () => {
    if (logoSrc !== '/logo.svg') setLogoSrc('/logo.svg');
  };

  const footerLogo = '/luther.png';

  const handleCopyColor = (formattedColor) => {
    // Extract hex from formatted color by finding the original hex in colors array
    const hex = colors.find(c => {
      const cHex = typeof c === 'string' ? c : c.hex;
      return formatColor(cHex, colorFormat) === formattedColor;
    });
    const hexValue = hex ? (typeof hex === 'string' ? hex : hex.hex) : null;
    showToast("Copied to clipboard: ", formattedColor, hexValue ? [hexValue] : []);
  };

  const copyAllColors = async () => {
    if (colors.length === 0) return;
    const formattedColors = colors.map(c => {
      const hex = typeof c === 'string' ? c : c.hex;
      return formatColor(hex, colorFormat);
    }).join(', ');
    const hexValues = colors.map(c => typeof c === 'string' ? c : c.hex);
    try {
      await navigator.clipboard.writeText(formattedColors);
      showToast("Copied to clipboard: ", formattedColors, hexValues);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const selectedFormat = FORMAT_OPTIONS.find(opt => opt.value === colorFormat);

  const toggleTooltip = theme === 'dark' ? 'Light Mode' : 'Dark Mode';

  const refreshPage = () => window.location.reload();
  const onBrandKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      refreshPage();
    }
  };

  return (
    <div className="app-container">
      <header className={`app-header ${isPageLoaded ? 'fade-in-blur' : ''}`}>
        <div className="header-inner">
          <div 
            className="brand" 
            onClick={refreshPage}
            onKeyDown={onBrandKeyDown}
            tabIndex={0}
            role="button"
            aria-label="Refresh page"
          >
            <div className="brand-logo-wrapper">
              <img src={logoSrc} alt="Colority" className="brand-logo-blur" onError={handleLogoError} />
              <img src={logoSrc} alt="Colority" className="brand-logo" onError={handleLogoError} />
            </div>
            <h1 className="app-title">Colority</h1>
          </div>
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={toggleTooltip}
            data-tooltip={toggleTooltip}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>
        </div>
      </header>
      <main className="main">
        <section className={`panel ${isPageLoaded ? 'fade-in-blur fade-delay-1' : ''}`}>
          <div className="upload-box">
            <UploadArea onImage={handleImage} imageUrl={imageUrl} isPageLoaded={isPageLoaded} onError={(msg) => {
              showToast(msg);
            }} onRemove={handleRemove} />
          </div>
        </section>
        <section className={`panel ${isPageLoaded ? 'fade-in-blur fade-delay-2' : ''}`}>
          <div className="panel-header">
            <h2>Colors</h2>
            {colors.length > 0 && (
              <div className="panel-header-actions">
                <div className="format-dropdown" ref={dropdownRef}>
                  <button
                    className="format-dropdown-toggle"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    aria-label="Select color format"
                    aria-expanded={dropdownOpen}
                    data-tooltip="Switch Format"
                  >
                    Format: <strong>{selectedFormat?.label || 'Hex'}</strong>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {dropdownOpen && (
                    <div className="format-dropdown-menu">
                      {FORMAT_OPTIONS.map(option => (
                        <button
                          key={option.value}
                          className={`format-dropdown-item ${colorFormat === option.value ? 'active' : ''}`}
                          onClick={() => {
                            setColorFormat(option.value);
                            setDropdownOpen(false);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className="copy-all-btn"
                  onClick={copyAllColors}
                  aria-label="Copy all colors"
                  data-tooltip="Copy All"
                >
                  <CopyIcon />
                </button>
              </div>
            )}
          </div>
          <ColorSwatches colors={colors} onCopy={handleCopyColor} isLoading={isLoading} colorFormat={colorFormat} isPageLoaded={isPageLoaded} />
        </section>
      </main>
      <footer className={`app-footer ${isPageLoaded ? 'fade-in-blur fade-delay-3' : ''}`}>
        <div className="footer-top-row">
          <div className="footer-left">
            <img className="footer-logo" src={footerLogo} alt="Luther Studio" />
            <span>By <a href="https://www.luther.studio/index.html" className="footer-brand-link" rel="noopener noreferrer" target="_blank">Luther Studio</a> © 2025</span>
          </div>
          <div className="footer-right">
            <div className="footer-links-group">
              <Link to="/imprint" className="footer-link">Imprint</Link>
              <Link to="/privacy-policy" className="footer-link">Privacy Policy</Link>
            </div>
            <BuyMeACoffeeButton
              showTooltip={showBMCTooltip}
              onMouseEnter={() => setShowBMCTooltip(true)}
              onMouseLeave={() => setShowBMCTooltip(false)}
            />
          </div>
        </div>
        <div className="footer-bottom-row">
          <BuyMeACoffeeButton
            className="bmc-mobile"
            showTooltip={showBMCTooltip}
            onMouseEnter={() => setShowBMCTooltip(true)}
            onMouseLeave={() => setShowBMCTooltip(false)}
          />
        </div>
      </footer>
      {toast && (
        <div className={`toast ${toastExiting ? 'exiting' : ''}`}>
          <CopyIcon />
          <span>
            {toast}
            {toastHex && toastColors.length > 0 ? (
              toastHex.split(', ').map((formattedColor, index) => {
                const hex = toastColors[index];
                if (!hex) return <strong key={index}>{formattedColor}</strong>;
                return (
                  <strong
                    key={index}
                    className="toast-color-code"
                    style={{
                      color: hex,
                    }}
                  >
                    {formattedColor}
                    {index < toastHex.split(', ').length - 1 ? ', ' : ''}
                  </strong>
                );
              })
            ) : (
              <strong>{toastHex}</strong>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
